import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, FileText, FileJson, Table, ShieldAlert, Globe, Lock, Shield, Plus, X, Bell } from "lucide-react";
import { getReminderPrefs, setReminderEnabled, setReminderTime, requestNotificationPermission } from "@/lib/reminders";

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [privacy, setPrivacy] = useState({ is_public: false, public_fields: { batting: true, bowling: true, bio: true } });
  const [clubs, setClubs] = useState([]);
  const [clubInput, setClubInput] = useState("");
  const [busy, setBusy] = useState("");
  const [reminder, setReminder] = useState(getReminderPrefs());

  useEffect(() => {
    api.get("/profile/me").then(({ data }) => {
      if (data.profile?.privacy) setPrivacy({ is_public: false, public_fields: { batting: true, bowling: true, bio: true }, ...data.profile.privacy });
      setClubs(data.profile?.clubs || []);
    });
  }, []);

  const savePrivacy = async (next) => { setPrivacy(next); await api.put("/profile", { privacy: next }); toast.success("Privacy updated"); };

  const saveClubs = async (next) => { setClubs(next); await api.put("/profile", { clubs: next }); };
  const addClub = async () => {
    const v = clubInput.trim();
    if (!v) return;
    if (clubs.some((c) => c.toLowerCase() === v.toLowerCase())) { toast.error("Club already added"); return; }
    await saveClubs([...clubs, v]); setClubInput(""); toast.success(`Added ${v}`);
  };
  const removeClub = async (i) => { await saveClubs(clubs.filter((_, j) => j !== i)); };

  const toggleReminder = async (on) => {
    if (on) {
      const perm = await requestNotificationPermission();
      if (perm === "unsupported") { toast.error(t("Notifications aren't supported on this device")); return; }
      if (perm !== "granted") { toast.error(t("Allow notifications to get match reminders")); return; }
    }
    setReminderEnabled(on);
    setReminder((r) => ({ ...r, enabled: on }));
    toast.success(on ? t("Match reminders on") : t("Match reminders off"));
  };
  const changeReminderTime = (val) => { setReminderTime(val); setReminder((r) => ({ ...r, time: val })); };

  const download = async (path, filename) => {
    setBusy(path);
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setBusy(""); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Delete your account and ALL data permanently? This cannot be undone.")) return;
    await api.delete("/account"); logout(); navigate("/"); toast.success("Account deleted");
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <h1 className="font-head font-bold text-3xl">{t("Settings")}</h1>

      <div className="ct-card p-6" data-testid="clubs-settings">
        <h2 className="font-head font-bold text-xl mb-1 flex items-center gap-2"><Shield size={18} className="text-brand" /> {t("My Clubs")}</h2>
        <p className="text-slate-500 text-sm mb-4">Play for more than one team? Add every club you turn out for — they'll show on your profile and public card.</p>
        <div className="flex gap-2">
          <Input value={clubInput} onChange={(e) => setClubInput(e.target.value)} placeholder="e.g. Shivaji Park Gymkhana" data-testid="club-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addClub(); } }} />
          <Button className="bg-brand hover:bg-brand-dark shrink-0" onClick={addClub} data-testid="add-club-btn"><Plus size={16} /> Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="clubs-list">
          {clubs.length === 0 ? <span className="text-sm text-slate-400">No clubs added yet.</span> : clubs.map((c, i) => (
            <span key={i} className="px-3 py-1.5 rounded-full bg-slate-900 text-white text-sm flex items-center gap-2" data-testid={`club-chip-${i}`}>
              {c}
              <button onClick={() => removeClub(i)} className="text-white/70 hover:text-white" data-testid={`remove-club-${i}`}><X size={14} /></button>
            </span>
          ))}
        </div>
      </div>

      <div className="ct-card p-6" data-testid="privacy-settings">
        <h2 className="font-head font-bold text-xl mb-1 flex items-center gap-2">{privacy.is_public ? <Globe size={18} className="text-green-600" /> : <Lock size={18} />} Privacy</h2>
        <p className="text-slate-500 text-sm mb-4">Reflections, audio notes, coach notes and goals are never public.</p>
        <div className="flex items-center justify-between py-3 border-b">
          <Label>Public profile</Label>
          <Switch checked={privacy.is_public} onCheckedChange={(v) => savePrivacy({ ...privacy, is_public: v })} data-testid="toggle-public" />
        </div>
        {privacy.is_public && ["batting", "bowling", "bio"].map((f) => (
          <div key={f} className="flex items-center justify-between py-3 border-b last:border-0">
            <Label className="capitalize">Show {f}</Label>
            <Switch checked={privacy.public_fields?.[f] ?? true} onCheckedChange={(v) => savePrivacy({ ...privacy, public_fields: { ...privacy.public_fields, [f]: v } })} data-testid={`toggle-public-${f}`} />
          </div>
        ))}
      </div>

      <div className="ct-card p-6" data-testid="reminder-settings">
        <h2 className="font-head font-bold text-xl mb-1 flex items-center gap-2"><Bell size={18} className="text-brand" /> {t("Match reminders")}</h2>
        <p className="text-slate-500 text-sm mb-4">{t("Get an evening nudge to log the day's match or practice while it's fresh.")}</p>
        <div className="flex items-center justify-between py-3 border-b">
          <Label>{t("Daily reminder")}</Label>
          <Switch checked={reminder.enabled} onCheckedChange={toggleReminder} data-testid="toggle-reminder" />
        </div>
        {reminder.enabled && (
          <div className="flex items-center justify-between py-3">
            <Label>{t("Remind me at")}</Label>
            <Input type="time" value={reminder.time} onChange={(e) => changeReminderTime(e.target.value)} className="w-32" data-testid="reminder-time" />
          </div>
        )}
      </div>

      <div className="ct-card p-6" data-testid="export-settings">
        <h2 className="font-head font-bold text-xl mb-4 flex items-center gap-2"><Download size={18} /> Export data</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Button variant="outline" onClick={() => download("/export/entries.csv", "cricktrack_entries.csv")} disabled={busy === "/export/entries.csv"} data-testid="export-csv-button"><Table size={15} /> Entries CSV</Button>
          <Button variant="outline" onClick={() => download("/export/entries.json", "cricktrack_entries.json")} data-testid="export-json-button"><FileJson size={15} /> Entries JSON</Button>
          <Button variant="outline" onClick={() => download("/export/season.pdf", "cricktrack_season_report.pdf")} disabled={busy === "/export/season.pdf"} data-testid="export-pdf-button"><FileText size={15} /> Season PDF</Button>
          <Button variant="outline" onClick={() => download("/export/all", "cricktrack_all_data.json")} data-testid="export-all-button"><Download size={15} /> Download all my data</Button>
        </div>
        <p className="text-xs text-slate-400 mt-3">Audio notes are exported as secure links/metadata only.</p>
      </div>

      <div className="ct-card p-6 border-brand/30" data-testid="danger-zone">
        <h2 className="font-head font-bold text-xl mb-2 flex items-center gap-2 text-brand"><ShieldAlert size={18} /> Danger zone</h2>
        <p className="text-slate-500 text-sm mb-4">Permanently delete your account and all associated data.</p>
        <Button variant="destructive" className="bg-brand hover:bg-brand-dark" onClick={deleteAccount} data-testid="delete-account-btn">Delete account</Button>
      </div>
    </div>
  );
}
